import ReactFlow, {
  Edge,
  Node,
  Handle,
  Position,
  Background,
  MiniMap,
  Controls,
  useReactFlow,
  ReactFlowProvider,
  useEdges,
  NodePositionChange,
  NodeChange,
  Connection,
  OnConnectStartParams,
  NodeProps,
} from "reactflow";
import { DiamondIcon } from "./icons/steps/DiamondIcon";
import { useTriggerEditorStateDispatch } from "../TriggerEditor/TriggerEditor";
import {
  addSteps,
  removeNode,
  saveDraft,
  selectEdge,
  selectNode,
} from "./reducer/actionCreators";
import { PlusIcon } from "./icons/PlusIcon";
import { WarningOutlined } from "@ant-design/icons";
import { nodeSizes, NODE_WIDTH } from "./createLayout";
import { TRIGGER_NAME_MAP } from "./triggerConstants";
import React, { useCallback, useEffect, useRef } from "react";
import clsx from "clsx";
import { PlaceholderData, TypedNode } from "./reducer/stateUtils";
import SimpleFloatingEdge, { EdgeButton } from "./FloatingEdge";
import "./style.css";
import "reactflow/dist/style.css";
import {
  TriggerForEditor,
  TriggerStepIf,
  isOfTypeSupportedStep,
  createPlaceholderStep,
} from "@dots-projects/types";
import { useDefinedPreviousValue } from "./useDefinedPreviousValue";

import { motion } from "framer-motion";

import { Tooltip } from "antd";
import { MutateTriggerAction } from "./reducer/reducerTypes";
import { nanoid } from "nanoid";
/**
 *
 * Component that renders four source and target React Flow handles (8 handles in total). These handles are the start and end point of edges in the flow chart.
 *
 * We add all 8 handles in order to support more flexible edge layout. E.g. For an edge coming out of a condition diamond and pointing to a step that's ABOVE the diamond, we want source (on the diamond) to be Position.Right, and target (on the step node) to be Position.Right.
 *
 */
function Handles({ showConnectHandle = false }) {
  const positions = [
    Position.Left,
    Position.Right,
    Position.Top,
    Position.Bottom,
  ];
  return (
    <>
      {/* These are handles that we internally use to show edges. These handles are always hidden to give the flow a clean look. */}
      {positions.map((p) => (
        <React.Fragment key={p}>
          <Handle className="invisible" type="source" position={p} id={p} />
          <Handle className="invisible" type="target" position={p} id={p} />
        </React.Fragment>
      ))}
      {/* This is the handle (black dot) that displays when you hover on the nodes. This is what we actually use to connect to other steps. 
      For some reason we need to make it a target handle to get things to work.*/}
      {showConnectHandle && (
        <Handle
          style={{ width: 20, height: 20, bottom: -9 }}
          // To make the handle appear on hover, we need to add "group" class to a parent, for example in StepNode
          className="opacity-0 group-hover:opacity-100 
          bg-black hover:bg-dots-dark-orange"
          type="target"
          position={Position.Bottom}
          id="DragHandle"
        />
      )}
    </>
  );
}

/**
 * React Flow node for conditionals (Diamond)
 */
const ConditionNode = ({
  data,
  selected,
  dragging,
}: {
  data: { label: string; draggable: boolean };
  selected: boolean;
  dragging: boolean;
}) => (
  <motion.div
    layout={!dragging}
    className={`flex justify-center`}
    style={{ width: NODE_WIDTH }}
  >
    <Handles />
    <div className="flex gap-2 items-center">
      <DiamondIcon
        stroke={selected ? 5 : 3}
        className={clsx(`w-16 h-16`, {
          "text-dots-vibrant-orange": selected,
          "text-black": !selected,
          dragging: dragging,
          draggable: data.draggable,
        })}
        label={data.label}
      />
    </div>
  </motion.div>
);

/**
 * React Flow node for if-else, the special idle node with if-condition (Diamond)
 */
const IfElseNode = ({
  data,
  selected,
  dragging,
}: {
  data: {
    if: TriggerStepIf;
    trigger: TriggerForEditor;
    isDraggedOver: boolean;
    draggable: boolean;
  };
  selected: boolean;
  dragging: boolean;
}) => {
  const getOptionLabel = (stepId: string, optionId: string) => {
    const step = data.trigger.steps.find((s) => s.id === stepId);
    const options =
      step?.type === "send_multiple_select" ? step?.configs.options : [];
    // console.log("!!!", { stepId, step, trigger, optionId, options });
    const option = options.find((o) => o.id === optionId);
    return option?.label ?? optionId;
  };

  // {"send_multiple_select-0-MSSvWm.userResponse.optionIds":{"array_contains":"asia"}}
  const regex =
    /\{"([\w-]+).userResponse.optionIds":\{"array_contains":"([\w-]+)"\}\}/;
  const conditionAsString = JSON.stringify(data.if.condition);
  const match = conditionAsString.match(regex);
  const condition = match
    ? getOptionLabel(match[1], match[2])
    : conditionAsString;
  const label = `If "${condition}"`;
  return (
    <motion.div
      layout={!dragging}
      className={clsx(`flex justify-center`, {
        dragging: dragging,
        draggable: data.draggable,
      })}
      style={{ width: NODE_WIDTH }}
    >
      <Handles />
      <div className="flex gap-2 items-center relative">
        <DiamondIcon
          stroke={selected ? 5 : 3}
          showDraggedOverHighlight={data.isDraggedOver}
          className={clsx(`w-16 h-16`, {
            "text-dots-vibrant-orange": selected,
            "text-black": !selected,
          })}
        />
        <div className="absolute bg-white bg-opacity-80 text-xs max-w-md whitespace-nowrap">
          {label}
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} id="a" />
    </motion.div>
  );
};

/**
 * React Flow node for a single trigger step
 */
const StepNode = ({
  id,
  data,
  selected,
  dragging,
}: {
  id: string;
  data: {
    displaySettings: { icon?: string; type: string };
    label: string;
    isDraggedOver: boolean;
    draggable: boolean;
  };
  selected: boolean;
  dragging: boolean;
}) => {
  const { state } = useTriggerEditorStateDispatch();
  const { errors } = state.editor;
  const stepId = errors?.stepId;
  const stepError = stepId === id;
  const statusStyles = {
    "node-selected": !stepError && selected,
    "border-black ": !stepError && !selected,
    "dragged-over": data.isDraggedOver,
    "border-red-500 border-4": stepError,
    draggging: dragging,
    draggable: data.draggable,
  };
  const showConnectHandle =
    !dragging && !data.isDraggedOver && state.trigger.current.isDraft;

  switch (data.displaySettings.type) {
    case "Delete a channel":
      return (
        <motion.div
          className="group"
          style={{ width: NODE_WIDTH }}
          layout={!dragging}
        >
          <Handles showConnectHandle={showConnectHandle} />
          <div
            className={clsx(
              "border-2 w-full text-ellipsis overflow-hidden bg-blue-100 rounded-full flex justify-center items-center",
              statusStyles
            )}
          >
            <div className="flex flex-col place-items-center h-24 p-6">
              <div>{data.displaySettings.icon}</div>
              <div className="wrap font-bold ">{data.displaySettings.type}</div>
            </div>
          </div>
        </motion.div>
      );
    default:
      return (
        <motion.div
          layout={!dragging}
          className={clsx(
            `group border-2 p-5 overflow-hidden rounded-lg bg-white hover:shadow-lg`,
            statusStyles
          )}
          style={{ width: NODE_WIDTH }}
        >
          {/* For debugging */}
          {/* <div className="absolute -top-1 left-0">
            ({props.xPos}, {props.yPos})
          </div>
          <div className="absolute -bottom-1 right-0">
            ({props.xPos + nodeSizes["step"].w},{" "}
            {props.yPos + nodeSizes["step"].h})
          </div> */}
          <Handles showConnectHandle={showConnectHandle} />
          <div className="flex flex-col gap-1 mb-2">
            <div className="flex gap-2 justify-between">
              {data.displaySettings.icon}{" "}
              {stepError ? (
                <Tooltip placement="top" title={"This step contains an error"}>
                  <WarningOutlined
                    style={{ color: "#f44336", fontSize: "23px" }}
                  ></WarningOutlined>
                </Tooltip>
              ) : (
                ""
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex gap-2">
              <div className="font-semibold">{data.displaySettings.type}</div>
            </div>
            <div
              style={{
                width: 150,
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
              className="text-left"
            >
              {data.label}
            </div>
          </div>
        </motion.div>
      );
  }
};

/**
 * React Flow node for placeholders in published mode ("." in the UI)
 */
const PlaceholderPublishedNode = ({}: {
  data: { parentId: string };
  selected: boolean;
}) => {
  return (
    <div className="flex justify-center" style={{ width: NODE_WIDTH }}>
      <Handles />
      <div
        className={`group border-2 p-0.5 rounded-full cursor-default border-black bg-black`}
      ></div>
    </div>
  );
};

/**
 * React Flow node for placeholders ("+" in the UI)
 */
const PlaceholderNode = ({
  selected,
  data: { isFloating, isFirstPlaceholder, draggable },
  id,
  dragging,
}: {
  id: string;
  data: PlaceholderData;
  selected: boolean;
  dragging: boolean;
}) => {
  const { dispatch } = useTriggerEditorStateDispatch();
  const edges = useEdges();
  const inMiddleOfEdge = edges.some((e) => e.source === id);
  // Remove floating placeholder when it's unselected
  const prevSelected = useDefinedPreviousValue(selected);
  useEffect(() => {
    if (isFloating && prevSelected && !selected) {
      dispatch(removeNode(id));
    }
  }, [isFloating, selected, prevSelected]);

  return isFirstPlaceholder ? (
    <div
      className={clsx(
        `border-2 p-5 overflow-hidden rounded-3xl bg-white hover:shadow-lg flex flex-col justify-center items-center text-xs bg-slate-50`,
        {
          "border-dots-vibrant-orange border-dashed": selected,
          "border-black border-dashed": !selected,
        }
      )}
      style={{ width: NODE_WIDTH, background: "#F8FAFC" }}
    >
      <Handles />
      <PlusIcon />
      <p className="m-0 mt-3">Click here to add an action</p>
      <p className="m-0">(such as send survey)</p>
    </div>
  ) : (
    <>
      {isFloating && (
        <div
          className={clsx("border rounded-full border-white", {
            "border-dots-vibrant-orange": selected,
          })}
          style={{ marginLeft: -1, marginTop: -1 }}
          onMouseLeave={() => !selected && dispatch(removeNode(id))}
        >
          <EdgeButton icon={<PlusIcon />} />
        </div>
      )}
      {!isFloating && (
        <motion.div
          layout={!dragging}
          className="flex justify-center"
          style={{ width: NODE_WIDTH }}
        >
          <Handles />
          <div
            className={clsx(
              `group border-2 p-0.5 rounded-full hover:border-dots-vibrant-orange relative`,
              {
                "border-dots-vibrant-orange": selected,
                "border-black hover:bg-dots-vibrant-orange":
                  !selected && !inMiddleOfEdge,
                "border-transparent": inMiddleOfEdge,
                draggable: draggable,
              }
            )}
          >
            {/* This is the faked edge when the placeholder ("+") is hidden. */}
            <div
              className={clsx(`absolute bg-black h-full mx-auto`, {
                hidden: !inMiddleOfEdge,
                "opacity-100 group-hover:opacity-0": inMiddleOfEdge,
              })}
              style={{ width: 2, left: 17, height: "130%", top: -5 }}
            />
            <PlusIcon
              className={clsx(`relative`, {
                "opacity-0 group-hover:opacity-100 ":
                  inMiddleOfEdge && !selected,
              })}
            />
            <div
              className={`hidden items-center absolute left-11 top-0 h-10 text-xs font-bold w-20  ${
                !selected && "group-hover:flex"
              }`}
            >
              {isFloating
                ? "Add an action in between"
                : draggable
                ? "Click or Drag"
                : "Click to add a new action"}
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
};

/**
 * React Flow node for the root of the trigger
 */
const TriggerRootNode = (node: Node) => {
  const { data, selected } = node;
  const { state } = useTriggerEditorStateDispatch();
  const { errors } = state.editor;
  const rootError = errors?.root;

  return (
    <div style={{ width: NODE_WIDTH }}>
      <Handles />
      <div
        className={clsx(
          `border-2 w-full text-ellipsis overflow-hidden bg-entry-green rounded-full flex justify-center items-center`,
          {
            "node-selected": !rootError && selected,
            "border-black ": !rootError && !selected,
            "border-red-500": rootError,
            dragging: node.dragging,
            draggable: data.draggable,
          }
        )}
      >
        <div className="flex flex-col place-items-center h-24 p-6">
          <div>
            <img src="/entry.svg" width={24}></img>
          </div>
          <div className="wrap font-bold ">
            {TRIGGER_NAME_MAP[data.label]
              ? TRIGGER_NAME_MAP[data.label]
              : data.label}{" "}
            {rootError ? (
              <Tooltip placement="top" title={"This node contains an error"}>
                <WarningOutlined style={{ color: "red" }}></WarningOutlined>
              </Tooltip>
            ) : (
              ""
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export type FlowNodeType =
  | "step"
  | "condition"
  | "placeholder"
  | "placeholder-published"
  | "trigger"
  | "if-else";

const nodeTypes: { [key in FlowNodeType]: any } = {
  condition: ConditionNode,
  step: StepNode,
  placeholder: PlaceholderNode,
  trigger: TriggerRootNode,
  "if-else": IfElseNode,
  "placeholder-published": PlaceholderPublishedNode,
};

const edgeTypes = {
  floating: SimpleFloatingEdge,
};

interface FlowProps {
  edges: Edge[];
  nodes: Node[];
  moveControls: boolean;
  resizeMinimap?: boolean;
  hideAttribution?: boolean;
}
/**
 * React Flow chart to display trigger data.
 * @property {Edge[]} edges - List of React Flow edge objects.
 * @property {Node[]} nodes - List of React Flow node objects.
 * @returns {JSX.Element}
 *
 * @see https://reactflow.dev/docs/introduction/
 */
const Flow = ({
  edges,
  nodes,
  moveControls,
  resizeMinimap = false,
  hideAttribution = true,
}: FlowProps) => {
  const isDataValid = nodes.length > 0;
  const { state, dispatch, onPersist } = useTriggerEditorStateDispatch();

  const proOptions = {
    // passing in the account property will enable hiding the attribution
    account: "paid-pro",
    // in combination with the account property, hideAttribution: true will remove the attribution
    hideAttribution,
  };

  const { setViewport, getViewport, project } = useReactFlow();
  useEffect(() => {
    if (state.editor.errors?.changeViewportToNode) {
      const currentViewport = getViewport();
      const node = state.editor.errors.changeViewportToNode;
      const xViewport = node.position.x * -1 * currentViewport.zoom;
      const yViewport = node.position.y * -1 * currentViewport.zoom;
      setViewport({
        /**
         * We calculate the new viewport position base on the position node
         * also we use the half of the viewport x and y to center it
         */
        x: xViewport,
        y: yViewport + (yViewport * 0.2 * -1) / currentViewport.zoom,
        zoom: currentViewport.zoom,
      });
      //Clear state
      dispatch({
        type: "change_viewport",
        payload: {},
      });
    }
  }, [state.editor.errors?.changeViewportToNode]);

  const commitNodeDragStop = useCallback(
    (nodeChange: NodePositionChange) => {
      const node = nodes.find((n) => n.id === nodeChange.id) as TypedNode;
      const savePosition = (node: TypedNode) => {
        let payload: Extract<
          MutateTriggerAction,
          { type: "save_position" }
        >["payload"];

        switch (node.type) {
          case "condition":
            payload = {
              nodeType: "condition",
              position: node.position,
              stepId: node.data.stepId,
            };
            break;
          case "trigger":
            payload = { nodeType: "trigger", position: node.position };
            break;
          default:
            payload = {
              nodeType: "step",
              position: node.position,
              stepId: node.id,
            };
            break;
        }
        dispatch({
          type: "save_position",
          payload,
        });
        dispatch(saveDraft(onPersist));
      };

      if (node.type === "placeholder") {
        // When the user releases the mouse button, we check if the placeholder node is over a step node.
        const targetNode = state.flow.nodes.find(
          (n) =>
            (n.type === "step" || n.type === "if-else") && n.data.isDraggedOver
        ) as TypedNode | undefined;
        if (targetNode) {
          // Dispatch `connect_step` which actually modifies `trigger` in the state.
          dispatch({
            type: "connect_step",
            payload: {
              sourceOrPlaceholderStepId: node.data.parentStepId,
              when: node.data.when,
              targetStepId: targetNode.id,
            },
          });
          dispatch(saveDraft(onPersist));
          return true;
        } else {
          if (state.trigger.current.steps.find((s) => s.id === node.id)) {
            savePosition(node);
            return true;
          } // else it's an implicit placeholder whose position cannot be saved
        }
      } else {
        savePosition(node);
        return true;
      }
      return false;
    },
    [nodes, dispatch, onPersist, state.flow.nodes, state.trigger.current.steps]
  );

  const onNodesChange = useCallback(
    (nodeChanges: NodeChange[]) => {
      if (state.trigger.current.isDraft) {
        nodeChanges.forEach((nodeChange) => {
          if (nodeChange.type === "position") {
            if (nodeChange.dragging && nodeChange.position) {
              dispatch({
                type: "move_node",
                payload: {
                  nodeChange,
                },
              });
            } else {
              commitNodeDragStop(nodeChange);
            }
          }
        });
      }
    },
    [dispatch, commitNodeDragStop, state.trigger.current.isDraft]
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      const { source, target } = connection;
      if (source && target && source !== target) {
        dispatch({
          type: "connect_step",
          // the source and target are reversed for some reason
          payload: {
            sourceOrPlaceholderStepId: target,
            targetStepId: source.replace("userResponse-", ""),
          },
        });
      }
    },
    [dispatch]
  );

  const nodeIdBeingConnectedRef = useRef<string | null>(null);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  const onConnectStart = useCallback((_, param: OnConnectStartParams) => {
    nodeIdBeingConnectedRef.current = param.nodeId;
  }, []);
  const isMouseEvent = (e: TouchEvent | MouseEvent): e is MouseEvent => {
    return e && "clientX" in e;
  };
  const onConnectEnd = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (isMouseEvent(event)) {
        const targetIsPane = (event.target as HTMLElement)?.classList.contains(
          "react-flow__pane"
        );
        if (targetIsPane) {
          const parentId = nodeIdBeingConnectedRef.current;
          const parentNode = state.flow.nodes.find(
            (n) => n.id === parentId
          ) as TypedNode;
          if (parentNode?.type === "step") {
            const placeholderStep = createPlaceholderStep(
              `-dragged-out-${nanoid(6)}`
            );
            const { top, left } =
              reactFlowWrapper.current?.getBoundingClientRect() || {
                top: 0,
                left: 0,
              };
            const position = project({
              x: event.clientX - left,
              y: event.clientY - top,
            });
            placeholderStep.configs = {
              $$editorPosition: {
                x: position.x - nodeSizes["placeholder"].w / 2,
                y: position.y - nodeSizes["placeholder"].h / 2,
              },
            };
            dispatch(
              addSteps([placeholderStep], {
                parentStepId: parentNode.id,
                relayout: false,
              })
            );
            dispatch(selectNode(placeholderStep.id));
          }
        }
        dispatch({
          type: "set_nodes_drag_over_flag",
          payload: { trueNodeIds: [], falseNodeIds: "rest" },
        });
        nodeIdBeingConnectedRef.current = null;
      }
    },
    [dispatch, state.flow.nodes, reactFlowWrapper]
  );
  const onMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (nodeIdBeingConnectedRef.current) {
        const targetIsPane = (event.target as HTMLElement).classList.contains(
          "react-flow__pane"
        );
        if (!targetIsPane) {
          const { top, left } =
            reactFlowWrapper.current?.getBoundingClientRect() || {
              top: 0,
              left: 0,
            };
          const { x, y } = project({
            x: event.clientX - left,
            y: event.clientY - top,
          });
          const node = state.flow.nodes.find((n) =>
            // reactFlow.isNodeIntersecting(n, { x, y, height: 1, width: 1 }) // This causes some errors when dragging on some nodes.
            {
              const tn = n as TypedNode;
              return (
                n.position.x <= x &&
                x <= n.position.x + nodeSizes[tn.type].w &&
                n.position.y <= y &&
                y <= n.position.y + nodeSizes[tn.type].h &&
                n.id !== nodeIdBeingConnectedRef.current
              );
            }
          );
          // console.log("!!!", {
          //   x,
          //   y,
          //   eventX: event.clientX,
          //   eventY: event.clientY,
          //   node,
          //   viewPort,
          // });
          if (node) {
            dispatch({
              type: "set_nodes_drag_over_flag",
              payload: {
                trueNodeIds: [node.id],
                falseNodeIds: "rest",
              },
            });
          }
        }
      }
    },
    [dispatch, state.flow.nodes, getViewport]
  );

  return (
    <section
      className="w-full h-full"
      data-testid="flow-container"
      ref={reactFlowWrapper}
    >
      {isDataValid ? (
        <ReactFlow
          proOptions={proOptions}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          nodes={nodes}
          edges={edges}
          fitView={true}
          defaultViewport={{ zoom: 1.1, x: 0, y: 0 }}
          // These can be used to limit panning
          translateExtent={[
            [-6800, -200],
            [Infinity, Infinity],
          ]}
          panOnScroll={true}
          nodesConnectable={state.trigger.current.isDraft}
          nodesDraggable={state.trigger.current.isDraft} // Note this doesn't seem to have any effect. I had to disable dragging on `onNodesChange`
          selectNodesOnDrag={false}
          elementsSelectable={false} // disable builtin selection
          onEdgeClick={(_, edge) => {
            dispatch(selectEdge(edge.id));
          }}
          onNodeClick={(_, node) => {
            dispatch(selectNode(node.id));
            const step = state.trigger.current.steps.find(
              (s) => s.id === node.id
            );

            // if an unsupported step node is clicked, we should show the warning
            if (
              step?.type &&
              !isOfTypeSupportedStep(step.type) &&
              state.editor.visibleErrorBoxId !== "validation-errors"
            ) {
              dispatch({
                type: "show_errors_box",
                payload: { errorBoxId: "unsupported-step" },
              });
            }

            // hide unsupported warning if other node is selected
            if (
              step?.type &&
              isOfTypeSupportedStep(step.type) &&
              state.editor.visibleErrorBoxId === "unsupported-step"
            ) {
              dispatch({ type: "hide_errors_box" });
            }
          }}
          onNodesChange={onNodesChange}
          onConnect={onConnect}
          connectionRadius={80}
          onConnectStart={onConnectStart}
          onConnectEnd={onConnectEnd}
          onMouseMove={onMouseMove}
          multiSelectionKeyCode={null}
          className="h-20"
        >
          <Background />
          <MiniMap
            nodeColor="black"
            nodeStrokeWidth={4}
            className={resizeMinimap ? "w-28 h-20" : ""}
          />
          <Controls
            showFitView={false}
            showInteractive={false}
            style={moveControls ? { left: "330px" } : {}}
          />
        </ReactFlow>
      ) : (
        <div className="">
          <h1 data-testid="invalid-data-notice">Invalid trigger data</h1>
        </div>
      )}
    </section>
  );
};

// We need to wrap the Flow component with the `ReactFlowProvider` if we want to
// access to ReactFlow internal state: https://v10.reactflow.dev/docs/examples/misc/provider/
const TriggerFlow = (flowProps: FlowProps) => {
  return (
    <ReactFlowProvider>
      <Flow {...flowProps} />
    </ReactFlowProvider>
  );
};

export default TriggerFlow;
